// ===================================================================
// KAIXO - QuickRechargeModal
// ===================================================================
// Modal compacto que se abre cuando el saldo del wallet está bajo o
// agotado durante el uso de Direct, fototraducciones, etc. Permite
// recargar saldo SIN salir de la app móvil.
//
// Flujo:
//   1) Detecta saldo del user vía GET /api/billing/wallet/me.
//   2) Si tiene PayPal guardado (paypalEmail), muestra el email enmascarado
//      arriba — el user identifica que es su PayPal habitual.
//   3) 3 packs rápidos: $5 / $10 / $25.
//   4) Al pulsar uno, renderiza PayPal Smart Buttons. PayPal recuerda al
//      user logueado (cookie del WebView) → 1 click confirma → captura.
//   5) Backend recibe POST /paypal-confirm con JWT (no necesita phone).
//   6) Saldo arriba, modal cierra, dispara evento "wallet-recharged".
// ===================================================================

import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import './QuickRechargeModal.css';

const PAYPAL_CLIENT_ID = 'AQlBL1ILuxtkxFhMEOPiGVwKlon5OCH973DQ9wRQXsW4xqbOk3HIrAvX4r7EoIdC2pfavKBAR3XrB8ec';

const PACKS = [
    { amount: '5.00',  name: 'Básico',   bonus: 0,    label: '$5' },
    { amount: '10.00', name: 'Popular',  bonus: 0,    label: '$10' },
    { amount: '25.00', name: 'Pro',      bonus: 1.25, label: '$25 + 5%' }
];

// Carga el SDK PayPal una sola vez por sesión.
let paypalScriptPromise = null;
function loadPaypal() {
    if (paypalScriptPromise) return paypalScriptPromise;
    paypalScriptPromise = new Promise((resolve, reject) => {
        if (window.paypal) return resolve(window.paypal);
        const s = document.createElement('script');
        s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&currency=USD&intent=capture&enable-funding=card`;
        s.onload = () => resolve(window.paypal);
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return paypalScriptPromise;
}

export default function QuickRechargeModal({ open, onClose, currentUser, reason = 'low' }) {
    const [walletInfo, setWalletInfo] = useState(null);
    const [selectedPack, setSelectedPack] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const btnContainerRef = useRef(null);

    // Cargar estado del wallet (saldo + paypal guardado)
    useEffect(() => {
        if (!open) return;
        setError('');
        setSuccess(false);
        setSelectedPack(null);
        api.get('/billing/wallet/me')
            .then(({ data }) => setWalletInfo(data))
            .catch(() => setError('No se pudo cargar el saldo'));
    }, [open]);

    // Cuando se elige un pack, renderizar PayPal Smart Buttons
    useEffect(() => {
        if (!open || !selectedPack) return;
        let cancelled = false;
        loadPaypal().then(paypal => {
            if (cancelled || !btnContainerRef.current) return;
            btnContainerRef.current.innerHTML = '';
            paypal.Buttons({
                style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'pay', height: 44 },
                createOrder: (data, actions) => {
                    return actions.order.create({
                        purchase_units: [{
                            amount: { value: selectedPack.amount, currency_code: 'USD' },
                            description: `KAIXO - Recarga rápida ${selectedPack.name} ($${selectedPack.amount})`,
                            custom_id: String(currentUser?._id || '')
                        }]
                    });
                },
                onApprove: (data, actions) => {
                    return actions.order.capture().then(() => {
                        // Confirmar al backend. El JWT del user va en el
                        // header Authorization (api.js lo añade auto).
                        return api.post('/billing/wallet/paypal-confirm', {
                            orderID: data.orderID,
                            amount: selectedPack.amount,
                            packName: selectedPack.name
                        }).then(({ data: res }) => {
                            if (res?.success) {
                                setSuccess(true);
                                try {
                                    window.dispatchEvent(new CustomEvent('wallet-recharged', {
                                        detail: { newBalance: res.newBalance, cents: res.cents }
                                    }));
                                } catch {}
                                setTimeout(() => onClose?.(), 1800);
                            } else {
                                setError(res?.message || 'Pago capturado pero no se pudo añadir al saldo. Soporte: ' + data.orderID);
                            }
                        });
                    });
                },
                onError: (err) => {
                    console.error('[QuickRecharge] PayPal error:', err);
                    setError('Error en la pasarela de pago. Reintenta.');
                }
            }).render(btnContainerRef.current);
        }).catch(() => setError('No se pudo cargar el sistema de pago'));
        return () => { cancelled = true; };
    }, [open, selectedPack, currentUser?._id, onClose]);

    if (!open) return null;

    const titleMap = {
        low:     'Saldo bajo',
        empty:   'Saldo agotado',
        manual:  'Recargar saldo'
    };

    const content = (
        <div className="kx-qr-overlay" onClick={(e) => { if (e.target.classList.contains('kx-qr-overlay')) onClose?.(); }}>
            <div className="kx-qr-modal" role="dialog" aria-labelledby="kx-qr-title">
                <button className="kx-qr-close" onClick={onClose} aria-label="Cerrar">×</button>
                <h2 id="kx-qr-title" className="kx-qr-title">{titleMap[reason] || titleMap.low}</h2>

                {walletInfo && (
                    <div className="kx-qr-balance">
                        <span className="kx-qr-balance-label">Saldo actual</span>
                        <span className="kx-qr-balance-value">${((walletInfo.walletBalanceCents || 0) / 100).toFixed(2)}</span>
                    </div>
                )}

                {walletInfo?.hasPaypalSaved && (
                    <div className="kx-qr-paypal-hint">
                        Pagarás con tu PayPal habitual: <strong>{walletInfo.paypalEmailMasked}</strong>
                    </div>
                )}

                {success ? (
                    <div className="kx-qr-success">
                        <div className="kx-qr-success-icon">✓</div>
                        <div className="kx-qr-success-text">¡Saldo añadido!</div>
                    </div>
                ) : !selectedPack ? (
                    <>
                        <p className="kx-qr-sub">Elige cuánto recargar:</p>
                        <div className="kx-qr-packs">
                            {PACKS.map(p => (
                                <button
                                    key={p.amount}
                                    className="kx-qr-pack-btn"
                                    onClick={() => setSelectedPack(p)}
                                >
                                    <span className="kx-qr-pack-amount">{p.label}</span>
                                    {p.bonus > 0 && <span className="kx-qr-pack-bonus">+${p.bonus.toFixed(2)} bonus</span>}
                                </button>
                            ))}
                        </div>
                        <p className="kx-qr-legal">
                            Pago seguro con tarjeta o PayPal. Cobro en USD; el banco aplica la conversión a tu moneda. El saldo no caduca y queda disponible para todas las funciones de KAIXO.
                        </p>
                    </>
                ) : (
                    <>
                        <div className="kx-qr-selected">
                            Pagando <strong>{selectedPack.label}</strong>
                            <button className="kx-qr-change" onClick={() => setSelectedPack(null)}>Cambiar</button>
                        </div>
                        <div className="kx-qr-paypal-btn" ref={btnContainerRef}></div>
                    </>
                )}

                {error && <div className="kx-qr-error">{error}</div>}
            </div>
        </div>
    );

    return ReactDOM.createPortal(content, document.body);
}
